/**
 * Web Storage that never throws (2026-09-25 gap closure).
 *
 * Private mode, disabled site data, quota errors and sandboxed frames can make
 * `localStorage` / `sessionStorage` (or even the property access itself)
 * throw. Every read/write goes through here so the app keeps working with
 * in-memory state only; each failure is logged, never swallowed silently.
 */

function createSafeStorage(label, getStore) {
  const warn = (op, key, err) => {
    console.warn(`${label}.${op}('${key}') failed; continuing without storage:`, err);
  };
  return {
    /** @returns {string|null} the stored value, or null when absent or unavailable. */
    get(key) {
      try {
        return getStore().getItem(key);
      } catch (err) {
        warn('get', key, err);
        return null;
      }
    },
    /** @returns {boolean} true when the value was written. */
    set(key, value) {
      try {
        getStore().setItem(key, value);
        return true;
      } catch (err) {
        warn('set', key, err);
        return false;
      }
    },
    /** @returns {boolean} true when the key was removed. */
    remove(key) {
      try {
        getStore().removeItem(key);
        return true;
      } catch (err) {
        warn('remove', key, err);
        return false;
      }
    },
  };
}

export const safeStorage = createSafeStorage('localStorage', () => window.localStorage);
export const safeSessionStorage = createSafeStorage('sessionStorage', () => window.sessionStorage);
